import {
  Badge,
  Banner,
  Button,
  Dialog,
  Empty,
  Input,
  InputArea,
  Link,
  Loader,
  Meter,
  Select,
  Surface,
  Table,
  Text,
} from '@cloudflare/kumo'
import {
  ArrowSquareOut,
  MagnifyingGlass,
  Medal,
  PencilSimple,
  Plus,
  Trash,
  X,
} from '@phosphor-icons/react'
import {
  GOLDEN_MARKETS,
  GOLDEN_SET_TARGET,
  goldenCurrency,
  PRICE_UNITS,
  PRODUCT_CATEGORIES,
  productCategoryLabel,
  type GoldenIngredient,
  type GoldenMarket,
  type GoldenProduct,
  type GoldenCoverageRow,
  type GoldenProductInput,
  type PriceUnit,
  type ProductCategoryId,
} from '@repo/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminApi } from '../api'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'

const CATEGORY_ITEMS = PRODUCT_CATEGORIES.map((c) => ({ label: c.label, value: c.id }))
const UNIT_ITEMS = PRICE_UNITS.map((u) => ({ label: u, value: u }))
const MARKET_LABEL: Record<GoldenMarket, string> = { nyc: '미국·NYC (USD)', kr: '한국 (KRW)' }
const MARKET_ITEMS = GOLDEN_MARKETS.map((m) => ({ label: MARKET_LABEL[m], value: m }))
const ALL = 'all'
const fmtPrice = (n: number, market: GoldenMarket) =>
  goldenCurrency(market) === 'KRW'
    ? `₩${Math.round(n).toLocaleString('en-US')}`
    : `$${n.toFixed(2)}`

type Notice = { kind: 'ok' | 'error'; text: string }
const toMessage = (err: unknown) => (err instanceof Error ? err.message : String(err))

/**
 * 골든셋 관리 — 실제로 팔리는 소규모 식품 제품 예시(기준 데이터)를 모은다.
 * 웹에서 찾거나 직접 넣고, 틀린 건 지운다. 전부 기준 데이터로 쓰인다.
 */
export function GoldenSetPage() {
  const getToken = useGetToken()
  const [items, setItems] = useState<GoldenProduct[] | null>(null)
  const [category, setCategory] = useState<string>(ALL)
  const [market, setMarket] = useState<string>(ALL)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [editing, setEditing] = useState<GoldenProduct | 'new' | null>(null)
  const [deleting, setDeleting] = useState<GoldenProduct | null>(null)

  const reload = useCallback(async () => {
    try {
      const { items } = await adminApi.goldenSet.list(getToken)
      setItems(items)
    } catch (err) {
      setNotice({ kind: 'error', text: toMessage(err) })
    }
  }, [getToken])

  useEffect(() => {
    void reload()
  }, [reload])

  const visible = useMemo(
    () =>
      (items ?? []).filter(
        (i) =>
          (category === ALL || i.categoryId === category) &&
          (market === ALL || i.market === market),
      ),
    [items, category, market],
  )
  const total = items?.length ?? 0
  const paged = usePagedList(visible)

  const remove = async (item: GoldenProduct) => {
    setBusyId(item.id)
    try {
      await adminApi.goldenSet.remove(getToken, item.id)
      setItems((prev) => prev?.filter((i) => i.id !== item.id) ?? prev)
      setDeleting(null)
    } catch (err) {
      setNotice({ kind: 'error', text: toMessage(err) })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="골든셋"
        description="실제로 팔리는 소규모 식품 제품의 판매가·용량·재료예요. 생성할 때 판매가 범위의 근거가 되고, 결과 화면의 '지금 팔리는 제품'에 나와요."
        actions={
          <>
            <Button variant="secondary" icon={<Plus />} onClick={() => setEditing('new')}>
              직접 추가
            </Button>
            <Button
              variant="primary"
              icon={<MagnifyingGlass />}
              onClick={() => setGenerateOpen(true)}
            >
              웹에서 찾기
            </Button>
          </>
        }
      />

      <HelpPanel
        id="golden-set"
        steps={[
          <>
            아래 <b>커버리지</b> 표에서 빨간 칸을 먼저 보세요. 카테고리×시장마다{' '}
            <b>가격 있는 제품이 3개 이상</b>이어야 생성할 때 판매가 범위를 줄 수 있어요. 모자라면 AI
            가 판매가를 지어내요.
          </>,
          <>
            모자란 칸은 <b>웹에서 찾기</b>로 채우세요. 카테고리·시장·개수를 고르면 웹에서 실제
            제품을 검색해 넣어요(웹 검색 비용 발생). 이미 있는 제품은 건너뛰어요.
          </>,
          <>틀린 제품(단종·가격 오류)은 지우거나 수정하세요. 전부 기준 데이터로 쓰여요.</>,
        ]}
        notes={[
          '시장 NYC = 영어·일본어·중국어 사용자(미국 시장), KR = 한국어 사용자.',
          '로컬 개발 DB 는 API 를 켤 때마다 dev 의 골든셋을 복사해 와요. 여기서 바꾼 건 그 환경에만 남아요.',
        ]}
      />

      <CoverageTable reloadKey={items?.length ?? 0} />

      <Surface className="rounded-lg p-5">
        <Meter
          label="모은 제품"
          value={Math.min(total, GOLDEN_SET_TARGET)}
          max={GOLDEN_SET_TARGET}
          customValue={`${total} / ${GOLDEN_SET_TARGET}`}
        />
      </Surface>

      {notice && (
        <Banner
          variant={notice.kind === 'ok' ? 'default' : 'error'}
          title={notice.kind === 'ok' ? '완료' : '문제가 생겼어요'}
          description={notice.text}
        />
      )}

      <div className="flex flex-wrap gap-2">
        <Select
          aria-label="카테고리"
          size="sm"
          className="min-w-56"
          value={category}
          onValueChange={(v: string | null) => setCategory(v ?? ALL)}
          items={[{ label: `전체 카테고리 (${total})`, value: ALL }, ...CATEGORY_ITEMS]}
        />
        <Select
          aria-label="시장"
          size="sm"
          className="min-w-40"
          value={market}
          onValueChange={(v: string | null) => setMarket(v ?? ALL)}
          items={[{ label: '전체 시장', value: ALL }, ...MARKET_ITEMS]}
        />
      </div>

      <Surface className="overflow-x-auto rounded-lg p-0">
        {items === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : visible.length === 0 ? (
          <Empty
            icon={<Medal size={40} className="text-kumo-inactive" />}
            title={items.length === 0 ? '아직 제품이 없어요' : '이 조건의 제품이 없어요'}
            description={
              items.length === 0
                ? '웹에서 실제 제품을 찾아 넣거나 직접 추가하세요.'
                : '다른 카테고리·시장을 골라 보세요.'
            }
            contents={
              items.length === 0 ? (
                <Button
                  variant="primary"
                  icon={<MagnifyingGlass />}
                  onClick={() => setGenerateOpen(true)}
                >
                  웹에서 찾기
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>제품</Table.Head>
                <Table.Head>카테고리</Table.Head>
                <Table.Head>시장</Table.Head>
                <Table.Head>판매가</Table.Head>
                <Table.Head>용량</Table.Head>
                <Table.Head>재료</Table.Head>
                <Table.Head>
                  <span className="sr-only">작업</span>
                </Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paged.slice.map((item) => {
                const priced = item.ingredients.filter((i) => i.unitPrice !== null).length
                return (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1.5 font-medium">
                          {item.productName}
                          {item.sourceUrl && (
                            <Link
                              href={item.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="plain"
                              aria-label="출처 열기"
                            >
                              <ArrowSquareOut size={14} />
                            </Link>
                          )}
                        </span>
                        <span className="text-kumo-subtle text-xs">{item.brand}</span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="outline">{productCategoryLabel(item.categoryId)}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={item.market === 'kr' ? 'teal' : 'blue'}>
                        {item.market === 'kr' ? 'KR' : 'NYC'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {item.retailPrice === null ? (
                        <span className="text-kumo-subtle">—</span>
                      ) : (
                        fmtPrice(item.retailPrice, item.market)
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {item.packSize || <span className="text-kumo-subtle">—</span>}
                    </Table.Cell>
                    <Table.Cell>
                      {item.ingredients.length === 0 ? (
                        <span className="text-kumo-subtle">—</span>
                      ) : (
                        <span title={item.ingredients.map((i) => i.name).join(', ')}>
                          {item.ingredients.length}
                          <span className="text-kumo-subtle"> · {priced} priced</span>
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          shape="square"
                          variant="ghost"
                          icon={<PencilSimple />}
                          aria-label="수정"
                          disabled={busyId === item.id}
                          onClick={() => setEditing(item)}
                        />
                        <Button
                          size="sm"
                          shape="square"
                          variant="ghost"
                          icon={<Trash />}
                          aria-label="삭제"
                          loading={busyId === item.id}
                          onClick={() => setDeleting(item)}
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table>
        )}
      </Surface>
      <TablePagination
        page={paged.page}
        setPage={paged.setPage}
        perPage={paged.perPage}
        total={paged.total}
      />

      <GenerateDialog
        open={generateOpen}
        defaultCategory={category === ALL ? undefined : (category as ProductCategoryId)}
        onOpenChange={setGenerateOpen}
        onDone={(added, skipped) => {
          setNotice({
            kind: 'ok',
            text:
              added.length === 0
                ? `새로 찾은 제품이 없어요 — ${skipped}개는 이미 있어요.`
                : `${added.length}개를 추가했어요${skipped ? ` (중복 ${skipped}개 건너뜀)` : ''}.`,
          })
          setItems((prev) => [...added, ...(prev ?? [])])
          if (added[0]) setCategory(added[0].categoryId)
        }}
      />

      <EditDialog
        item={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={(saved, created) => {
          setItems((prev) =>
            created
              ? [saved, ...(prev ?? [])]
              : (prev?.map((i) => (i.id === saved.id ? saved : i)) ?? prev),
          )
          setEditing(null)
        }}
      />

      <Dialog.Root
        role="alertdialog"
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <Dialog size="sm" className="p-6">
          <Dialog.Title className="text-lg font-semibold">이 제품을 삭제할까요?</Dialog.Title>
          <Dialog.Description className="text-kumo-subtle mt-2">
            {deleting?.brand} 의 {deleting?.productName} 을(를) 골든셋에서 지워요. 되돌릴 수 없어요.
          </Dialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              취소
            </Button>
            <Button
              variant="destructive"
              loading={busyId === deleting?.id}
              onClick={() => deleting && void remove(deleting)}
            >
              삭제
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  )
}

// ── 웹 검색 다이얼로그 ────────────────────────────────────────────

function GenerateDialog(props: {
  open: boolean
  defaultCategory: ProductCategoryId | undefined
  onOpenChange: (open: boolean) => void
  onDone: (added: GoldenProduct[], skipped: number) => void
}) {
  const getToken = useGetToken()
  const [categoryId, setCategoryId] = useState<ProductCategoryId>(props.defaultCategory ?? 'sauce')
  const [market, setMarket] = useState<GoldenMarket>('nyc')
  const [count, setCount] = useState(5)
  const [hint, setHint] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (props.open && props.defaultCategory) setCategoryId(props.defaultCategory)
  }, [props.open, props.defaultCategory])

  const run = async () => {
    setBusy(true)
    setError(null)
    try {
      const { items, skipped } = await adminApi.goldenSet.generate(getToken, {
        categoryId,
        market,
        count,
        ...(hint.trim() ? { hint: hint.trim() } : {}),
      })
      props.onDone(items, skipped)
      props.onOpenChange(false)
      setHint('')
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog.Root open={props.open} onOpenChange={(open) => !busy && props.onOpenChange(open)}>
      <Dialog size="lg" className="p-6">
        <Dialog.Title className="text-lg font-semibold">실제 제품 찾기</Dialog.Title>
        <Dialog.Description className="text-kumo-subtle mt-1">
          웹에서 소규모 브랜드 제품을 찾아 판매가·용량·재료 단가를 가져와요. 이미 있는 제품은
          건너뛰어요. 15~40초 걸리고 웹 검색 비용이 들어요.
        </Dialog.Description>
        <div className="mt-5 flex flex-col gap-4">
          <Select
            label="카테고리"
            value={categoryId}
            onValueChange={(v: ProductCategoryId | null) => v && setCategoryId(v)}
            items={CATEGORY_ITEMS}
          />
          <Select
            label="시장"
            value={market}
            onValueChange={(v: GoldenMarket | null) => v && setMarket(v)}
            items={MARKET_ITEMS}
          />
          <Input
            label="개수"
            type="number"
            min={1}
            max={10}
            value={String(count)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCount(Math.max(1, Math.min(10, Number(e.target.value) || 1)))
            }
          />
          <InputArea
            label="조건 (선택)"
            required={false}
            placeholder='예: "비건", "1만원 이하", "소규모 브랜드"'
            value={hint}
            onValueChange={setHint}
            autoResize
            minRows={2}
          />
          {error && <Banner variant="error" title="검색하지 못했어요" description={error} />}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={() => props.onOpenChange(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            icon={<MagnifyingGlass />}
            loading={busy}
            onClick={() => void run()}
          >
            {busy ? '찾는 중…' : '찾기'}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  )
}

// ── 추가/수정 다이얼로그 ────────────────────────────────────────────────

type IngredientForm = { name: string; unit: PriceUnit; unitPriceUsd: string }

type FormState = {
  categoryId: ProductCategoryId
  market: GoldenMarket
  productName: string
  brand: string
  sourceUrl: string
  retailPriceUsd: string
  packSize: string
  ingredients: IngredientForm[]
  positioning: string
}

const EMPTY_INGREDIENT: IngredientForm = { name: '', unit: 'kg', unitPriceUsd: '' }
const EMPTY_FORM: FormState = {
  categoryId: 'sauce',
  market: 'nyc',
  productName: '',
  brand: '',
  sourceUrl: '',
  retailPriceUsd: '',
  packSize: '',
  ingredients: [{ ...EMPTY_INGREDIENT }],
  positioning: '',
}

const numOrEmpty = (n: number | null) => (n === null ? '' : String(n))
const parsePrice = (s: string) => {
  const n = Number.parseFloat(s)
  return Number.isFinite(n) && n > 0 ? n : null
}

function toForm(item: GoldenProduct): FormState {
  return {
    categoryId: item.categoryId,
    market: item.market,
    productName: item.productName,
    brand: item.brand,
    sourceUrl: item.sourceUrl ?? '',
    retailPriceUsd: numOrEmpty(item.retailPrice),
    packSize: item.packSize,
    ingredients: item.ingredients.length
      ? item.ingredients.map((i) => ({
          name: i.name,
          unit: i.unit,
          unitPriceUsd: numOrEmpty(i.unitPrice),
        }))
      : [{ ...EMPTY_INGREDIENT }],
    positioning: item.positioning,
  }
}

function toInput(form: FormState): GoldenProductInput {
  const ingredients: GoldenIngredient[] = form.ingredients
    .filter((i) => i.name.trim())
    .map((i) => ({ name: i.name.trim(), unit: i.unit, unitPrice: parsePrice(i.unitPriceUsd) }))
  return {
    categoryId: form.categoryId,
    market: form.market,
    productName: form.productName.trim(),
    brand: form.brand.trim(),
    sourceUrl: form.sourceUrl.trim() || null,
    retailPrice: parsePrice(form.retailPriceUsd),
    packSize: form.packSize.trim(),
    ingredients,
    positioning: form.positioning.trim(),
  }
}

function EditDialog(props: {
  item: GoldenProduct | 'new' | null
  onOpenChange: (open: boolean) => void
  onSaved: (item: GoldenProduct, created: boolean) => void
}) {
  const getToken = useGetToken()
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const open = props.item !== null
  const isNew = props.item === 'new'

  useEffect(() => {
    if (props.item === null) return
    setForm(props.item === 'new' ? EMPTY_FORM : toForm(props.item))
    setError(null)
  }, [props.item])

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setForm((f) => ({ ...f, [key]: value }))
  const onText = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(key)(e.target.value as never)
  const setIngredient = (index: number, patch: Partial<IngredientForm>) =>
    setForm((f) => ({
      ...f,
      ingredients: f.ingredients.map((i, n) => (n === index ? { ...i, ...patch } : i)),
    }))
  const removeIngredient = (index: number) =>
    setForm((f) => ({
      ...f,
      ingredients:
        f.ingredients.length === 1
          ? [{ ...EMPTY_INGREDIENT }]
          : f.ingredients.filter((_, n) => n !== index),
    }))
  const addIngredient = () =>
    setForm((f) => ({ ...f, ingredients: [...f.ingredients, { ...EMPTY_INGREDIENT }] }))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const input = toInput(form)
      if (props.item === 'new') {
        const { item } = await adminApi.goldenSet.create(getToken, input)
        props.onSaved(item, true)
      } else if (props.item) {
        const { item } = await adminApi.goldenSet.update(getToken, props.item.id, input)
        props.onSaved(item, false)
      }
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const valid = form.productName.trim() && form.brand.trim()

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !busy && props.onOpenChange(o)}>
      <Dialog size="xl" className="max-h-[90vh] overflow-y-auto p-6">
        <Dialog.Title className="text-lg font-semibold">
          {isNew ? '제품 추가' : '제품 수정'}
        </Dialog.Title>
        <Dialog.Description className="text-kumo-subtle mt-1">
          지금 실제로 팔리는 제품이어야 해요. 모르는 값은 추측하지 말고 비워 두세요.
        </Dialog.Description>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Select
            label="카테고리"
            value={form.categoryId}
            onValueChange={(v: ProductCategoryId | null) => v && set('categoryId')(v)}
            items={CATEGORY_ITEMS}
          />
          <Select
            label="시장"
            value={form.market}
            onValueChange={(v: GoldenMarket | null) => v && set('market')(v)}
            items={MARKET_ITEMS}
          />
          <Input label="제품명" value={form.productName} onChange={onText('productName')} />
          <Input label="브랜드" value={form.brand} onChange={onText('brand')} />
          <Input
            label="출처 URL"
            required={false}
            type="url"
            placeholder="https://"
            value={form.sourceUrl}
            onChange={onText('sourceUrl')}
          />
          <Input
            label={`판매가 (${goldenCurrency(form.market)})`}
            required={false}
            type="number"
            min={0}
            step="0.01"
            value={form.retailPriceUsd}
            onChange={onText('retailPriceUsd')}
          />
          <Input
            label="용량"
            required={false}
            placeholder="예: 250 g 병"
            value={form.packSize}
            onChange={onText('packSize')}
          />
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <div className="flex items-baseline justify-between">
            <Text variant="heading">재료</Text>
            <Text variant="secondary" size="sm">
              재료명과 단위당 시세 ({goldenCurrency(form.market)}). 예: 꿀 · 9.50 / kg
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            {form.ingredients.map((ing, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_7rem_5rem_2.25rem] items-center gap-2"
              >
                <Input
                  aria-label="재료"
                  placeholder="재료명"
                  size="sm"
                  value={ing.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIngredient(index, { name: e.target.value })
                  }
                />
                <Input
                  aria-label="단위당 가격"
                  placeholder={`${goldenCurrency(form.market)} / 단위`}
                  size="sm"
                  type="number"
                  min={0}
                  step="0.01"
                  value={ing.unitPriceUsd}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIngredient(index, { unitPriceUsd: e.target.value })
                  }
                />
                <Select
                  aria-label="단위"
                  size="sm"
                  value={ing.unit}
                  onValueChange={(v: PriceUnit | null) => v && setIngredient(index, { unit: v })}
                  items={UNIT_ITEMS}
                />
                <Button
                  size="sm"
                  shape="square"
                  variant="ghost"
                  icon={<X />}
                  aria-label="재료 삭제"
                  onClick={() => removeIngredient(index)}
                />
              </div>
            ))}
          </div>
          <Button
            size="sm"
            variant="secondary"
            icon={<Plus />}
            className="self-start"
            onClick={addIngredient}
          >
            재료 추가
          </Button>
        </div>

        <div className="mt-6">
          <InputArea
            label="포지셔닝"
            required={false}
            description="One sentence on why people buy it."
            value={form.positioning}
            onValueChange={set('positioning')}
            autoResize
            minRows={2}
          />
        </div>

        {error && (
          <div className="mt-4">
            <Banner variant="error" title="저장하지 못했어요" description={error} />
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={() => props.onOpenChange(false)}>
            취소
          </Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void save()}>
            {isNew ? '추가' : '저장'}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  )
}

/** 카테고리 × 시장 커버리지 — 가격 있는 제품이 3개 미만인 칸은 생성 때 판매가 범위를 못 준다 */
function CoverageTable({ reloadKey }: { reloadKey: number }) {
  const getToken = useGetToken()
  const [rows, setRows] = useState<GoldenCoverageRow[] | null>(null)
  useEffect(() => {
    let alive = true
    adminApi
      .goldenCoverage(getToken)
      .then((r) => alive && setRows(r.items))
      .catch(() => alive && setRows([]))
    return () => {
      alive = false
    }
  }, [getToken, reloadKey])
  if (rows === null) return null
  const missing = rows.filter((r) => !r.ok)
  const byCategory = new Map<string, GoldenCoverageRow[]>()
  for (const r of rows) byCategory.set(r.categoryId, [...(byCategory.get(r.categoryId) ?? []), r])
  return (
    <Surface className="flex flex-col gap-3 rounded-lg p-0" data-testid="golden-coverage">
      <div className="px-5 pt-5">
        <Text variant="heading">커버리지</Text>
        <Text variant="secondary" size="sm">
          {missing.length === 0
            ? '모든 카테고리·시장에 가격 있는 제품이 3개 이상 있어요.'
            : `가격 있는 제품이 3개 미만인 칸이 ${missing.length}개 있어요 — 그 조합은 판매가를 AI 가 추정해요.`}
        </Text>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <Table.Header variant="compact">
            <Table.Row>
              <Table.Head>카테고리</Table.Head>
              {GOLDEN_MARKETS.map((m) => (
                <Table.Head key={m}>{MARKET_LABEL[m]} · 가격 있음 / 전체</Table.Head>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {[...byCategory.entries()].map(([categoryId, cells]) => (
              <Table.Row key={categoryId}>
                <Table.Cell className="font-medium">
                  {productCategoryLabel(categoryId as ProductCategoryId)}
                </Table.Cell>
                {GOLDEN_MARKETS.map((m) => {
                  const c = cells.find((x) => x.market === m)
                  return (
                    <Table.Cell key={m} className="tabular-nums">
                      <Badge variant={c?.ok ? 'success' : 'error'}>
                        {c?.priced ?? 0} / {c?.count ?? 0}
                      </Badge>
                    </Table.Cell>
                  )
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </Surface>
  )
}
